import React, {useEffect, useRef, useState, useContext, useCallback, useMemo} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import {Button} from '../../Form/Button/Button';
import {buttonColors} from '../../Pickers/InputTheme';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import {PageTitle} from "../../PageTitle/PageTitle";
import './WeezBeeCourseEditor.scss';
import {WeezBeeCourse} from "../../WeezBeeCourse/WeezBeeCourse";
import { ComponentBank } from "../../WeezBeeCourse/components/ContentPane/ChapterEditor/ComponentBank";
import VisibilityIcon from '@mui/icons-material/Visibility';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import {CourseEditorProvider, CourseEditorContext, COURSE_MODES} from "../../../contexts/CourseEditorContext";
import {generateCourseWithAIAPI, checkCourseAIGenerationStatusAPI, updateWeezBeeCourseAPI} from "../../../Services/api";
import {CreateWithAIButton} from "./OpenAIWizardButton/CreateWithAIButton";
import {AICourseGeneratorWizard} from "./AICourseGeneratorWizard/AICourseGeneratorWizard";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {UserReactContext} from "../../../contexts/UserContext";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {FeatureFlags} from "../../../Utils/Constants";
import {AddSectionModal} from "./AddSectionModal";
import {CourseTemplateBank} from "../../WeezBeeCourse/components/ContentPane/ChapterEditor/CourseTemplateBank";
import {SA_createPublicTemplateAPI, SA_updatePublicTemplateAPI, SA_getPublicTemplateByIdAPI} from "../../../Services/SA_PublicTemplates_api";
import {MainActionButton} from "../../Form/DominantButton/MainActionButton";
import saveIcon from '../../../assets/images/save-full-icon.png';
import {useNavigateAfterSignDocSave} from "../../../Hooks/useNavigateAfterSignDocSave";

// Inner component that uses the context
const WeezBeeCourseEditorContent = ({courseTitle, courseDescription, signDocId, setIsTemplateMode, templateData, setTemplateData}) => {
    const {i18n, isRtl} = useContext(I18nReactContext);
    const navigate = useNavigate();
    const navigateAfterSignDocSave = useNavigateAfterSignDocSave();
    const {
        sections,
        selectedSection,
        isLoading,
        setIsLoading,
        addSection,
        selectSection,
        setGeneratingChapter,
        selectLastCreatedChapter,
        handleAIChapterCreated,
        setAiGenerationStatus,
        fetchCourseData,
        templateId,
        isTemplateMode,
        isFullScreen,
        setFullScreen,
        mode,
        setMode
    } = useContext(CourseEditorContext);

    const companyContext = useContext(CompanyReactContext);
    const userContext = useContext(UserReactContext);
    const isSuperAdmin = userContext.isUserSuperAdmin();

    // Update templateData when courseTitle or courseDescription changes
    useEffect(() => {
        setTemplateData(prevData => ({
            ...prevData,
            templateName: courseTitle || prevData.templateName,
            description: courseDescription || prevData.description
        }));
    }, [courseTitle, courseDescription]);

    // If user is super admin but not in template mode, ignore the AI feature flag
    // For super admin in template mode, we don't want to show the AI generation row
    // Also handle the case when featureFlags might be null or undefined
    const hasAIFF = isSuperAdmin && !isTemplateMode ? true : companyContext?.featureFlags?.[FeatureFlags.AI];

    const [showAddSectionModal, setShowAddSectionModal] = useState(false);
    const [isCourseTemplateBankOpen, setIsCourseTemplateBankOpen] = useState(false);
    const [userCourseDescription, setUserCourseDescription] = useState("");
    const [isAIWizardOpen, setIsAIWizardOpen] = useState(false);
    // Editor width and dragging state removed as the resizable divider has been removed
    const [isAILoading, setIsAILoading] = useState(false);
    const [pollingInterval, setPollingInterval] = useState(null);
    const editorContentRef = useRef(null);
    // editorWidthRef removed as the resizable divider has been removed
    const coursePreviewRef = useRef(null);

    const closeAddSectionModal = () => {
        setShowAddSectionModal(false);
    }

    const openCourseTemplateBank = (description) => {
        setUserCourseDescription(description);
        setIsCourseTemplateBankOpen(true);
    };

    const closeCourseTemplateBank = () => {
        setIsCourseTemplateBankOpen(false);
    };

    const handleSelectCourseTemplate = (courseTemplate) => {
        // Process the selected course template
        console.log('Selected course template:', courseTemplate);

        // Close the modal
        closeCourseTemplateBank();

        // If we have a signDocId from the template generation, start polling
        if (courseTemplate && courseTemplate.signDocId) {
            // Set loading state
            setIsAILoading(true);
            setAiGenerationStatus(null);

            // Set the generating chapter state to show the animation
            setGeneratingChapter(true);

            // Start polling for generation status
            startPolling(courseTemplate.signDocId);
        }
    };

    // Function to generate course with AI
    const generateCourseWithAI = async ({description, courseLengthMinutes}) => {
        try {
            setIsAILoading(true);
            setAiGenerationStatus(null);

            // Set the generating chapter state to show the animation
            setGeneratingChapter(true);

            // Start the AI generation process
            await generateCourseWithAIAPI({
                signDocId,
                description,
                courseLengthMinutes
            });

            // Start polling for generation status
            startPolling(signDocId);
        } catch (error) {
            console.error('Error generating course with AI:', error);
            setIsAILoading(false);
            setGeneratingChapter(false);
        }
    };

    // Function to start polling for AI generation status
    const startPolling = (signDocId) => {
        // Clear any existing polling interval
        if (pollingInterval) {
            clearInterval(pollingInterval);
        }

        // Keep track of the last known chapter count
        let lastChapterCount = 0;

        // Set up a new polling interval
        const interval = setInterval(async () => {
            try {
                const status = await checkCourseAIGenerationStatusAPI({signDocId});
                setAiGenerationStatus(status);

                // Check if the number of chapters has changed
                if (status && status.chaptersGenerated !== lastChapterCount) {
                    // Update the last known chapter count
                    lastChapterCount = status.chaptersGenerated;

                    // Refresh course data to get the new chapter
                    try {
                        const updatedSections = await fetchCourseData({ preserveSelection: true });

                        // Get the latest chapter and use handleAIChapterCreated for animations
                        if (updatedSections && updatedSections.length > 0) {
                            const latestChapter = updatedSections[updatedSections.length - 1];
                            if (latestChapter && handleAIChapterCreated) {
                                handleAIChapterCreated(latestChapter);
                            } else if (latestChapter) {
                                // Fallback to direct selection if handleAIChapterCreated is not available
                                selectSection(latestChapter.id);
                                setGeneratingChapter(true);
                            }
                        }
                    } catch (refreshError) {
                        console.error('Error refreshing course data:', refreshError);
                    }
                }

                // If generation is complete, stop polling
                if (status && status.status === 'COMPLETED') {
                    clearInterval(interval);
                    setPollingInterval(null);
                    setIsAILoading(false);

                    // Reset the generating chapter state
                    setGeneratingChapter(false);

                    // Select the last created chapter
                    selectLastCreatedChapter();
                }
            } catch (error) {
                console.error('Error checking AI generation status:', error);

                // Reset the generating chapter state in case of error
                setGeneratingChapter(false);
            }
        }, 3000); // Poll every 3 seconds

        setPollingInterval(interval);
    };

    // Clean up polling interval when component unmounts
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
                // Reset the generating state when cleaning up
                setGeneratingChapter(false);
            }
        };
    }, [pollingInterval, setGeneratingChapter]);

    // Mouse event handlers for resizing removed as the resizable divider has been removed

    // handleDragStart function removed as the resizable divider has been removed

    // toggleFullWidth function removed as the button has been removed

    // Toggle between admin preview mode and edit mode
    const toggleViewMode = useCallback(() => {
        const newMode = mode === COURSE_MODES.ADMIN_VIEW ? COURSE_MODES.EDIT : COURSE_MODES.ADMIN_VIEW;
        setMode(newMode);
    }, [setMode, mode]);


    const handleAddSection = () => {
        setShowAddSectionModal(true);
    };

    const handleSaveNewSection = (newSection) => {
        // Add the new section - it will be automatically selected by the addSection function
        addSection(newSection);
        setShowAddSectionModal(false);

        // Scroll to the edit section after a short delay to ensure the UI has updated
        setTimeout(() => {
            // Ensure the section is selected (in case the automatic selection didn't work)
            if (newSection && newSection.id) {
                selectSection(newSection.id);
            }

            // Scroll to the course preview
            if (coursePreviewRef.current) {
                coursePreviewRef.current.scrollIntoView({behavior: 'smooth'});
            }
        }, 200); // Increased delay to ensure the context has updated
    };

    

    // Add component via the external ComponentBank (next to the preview)
    // Delegate to the editor via a custom event so it selects the item immediately
    const handleAddComponentFromBank = useCallback((type) => {
        window.dispatchEvent(new CustomEvent('weezbee:add-component', { detail: { type } }));
    }, []);

    /**
     * Manual save handler for the course
     * Directly saves the course/template to the server and navigates to appropriate location
     */
    const handleSaveCourse = useCallback(async () => {
        try {
            setIsLoading(true);

            if (isTemplateMode) {
                // If in template mode, update the template
                const effectiveTemplateId = templateId || signDocId;
                await SA_updatePublicTemplateAPI({
                    id: effectiveTemplateId,
                    sections
                });
            } else {
                // Otherwise, update the course
                await updateWeezBeeCourseAPI({
                    signDocId,
                    sections
                });
            }

            // The timeout is used for user experience
            setTimeout(() => {
                if (isTemplateMode) {
                    // Navigate to template list
                    navigate('/nihol/courseTemplates', { replace: true });
                } else {
                    // Navigate to content page after saving
                    navigateAfterSignDocSave({
                        signDocId,
                        navigateToTab: true
                    });
                }
            }, 500);
        } catch (error) {
            console.error('Error saving course/template:', error);
            alert(i18n.translate('weezbeeCourse.editor.fetchError'));
            setIsLoading(false);
        }
    }, [sections, signDocId, isTemplateMode, templateId, setIsLoading, i18n, navigate, navigateAfterSignDocSave]);

    // Function to save the template to the database
    const handleSaveTemplate = async () => {
        if (!templateData.templateName || !templateData.templateSubject) {
            alert(i18n.translate('weezbeeCourse.editor.templateValidationError'));
            return;
        }

        try {
            // Set loading state
            setIsLoading(true);

            // Get the template ID from the context or URL params
            const urlParams = new URLSearchParams(window.location.search);
            const urlTemplateId = urlParams.get('templateId');
            const effectiveTemplateId = templateId || urlTemplateId;

            // Prepare the template data
            const template = {
                ...templateData,
                sections: sections,
                createdByUserId: userContext.user.id,
                // Include additional fields that might be needed
                estimatedDuration: templateData.estimatedDuration || 60, // Default to 60 minutes if not specified
                metadata: templateData.metadata || {}
            };

            let response;

            if (effectiveTemplateId) {
                // If we have a template ID, update the existing template
                template.id = effectiveTemplateId;
                response = await SA_updatePublicTemplateAPI(template);

                // Show success message
                alert(i18n.translate('weezbeeCourse.editor.templateUpdated'));
            } else {
                // Otherwise, create a new template
                response = await SA_createPublicTemplateAPI(template);

                // Show success message
                alert(i18n.translate('weezbeeCourse.editor.templateSaved'));

                // If we created a new template, update the URL to include the template ID
                if (response && response.id) {
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('templateId', response.id);
                    window.history.pushState({}, '', newUrl.toString());
                }
            }

        } catch (error) {
            console.error('Error saving template:', error);
            alert(i18n.translate('weezbeeCourse.editor.templateSaveError'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="weezbee-course-editor MenuPage">
            {isLoading ? <LoadingSpinner/> : ""}
            {/* Add Section Modal */}
            <AddSectionModal
                open={showAddSectionModal}
                onClose={closeAddSectionModal}
                onSave={handleSaveNewSection}
            />
            {/* AI Course Generator Wizard */}
            <AICourseGeneratorWizard
                open={isAIWizardOpen}
                onClose={() => setIsAIWizardOpen(false)}
                signDocId={signDocId}
                onSelectTemplate={(wizardData) => {
                    // Handle template selection from wizard - will be implemented in Step 4
                    setIsAIWizardOpen(false);
                    openCourseTemplateBank(wizardData.subject);
                }}
                onGenerationStart={() => {
                    // Start polling when generation starts from wizard
                    setIsAILoading(true);
                    setAiGenerationStatus(null);
                    setGeneratingChapter(true);
                    startPolling(signDocId);
                }}
            />
            {/* Always-open vertical component bank next to the preview */}
            <ComponentBank onAddComponent={handleAddComponentFromBank} isRtl={isRtl} />
            <PageTitle
                title={courseTitle || i18n.translate('weezbeeCourse.editor.title')}
                subTitle={courseDescription || ''}
                noLogo={true}
                size="minimized"
                customComponent={
                    <MainActionButton
                        title={i18n.translate('weezbeeCourse.editor.saveCourse')}
                        subTitle={i18n.translate('weezbeeCourse.editor.saveCourseSubTitle')}
                        image={saveIcon}
                        onClick={handleSaveCourse}
                    />
                }
            />
            <div className="menuInnerPage">
                {/* AI Course Generation */}
                {/* {hasAIFF && (
                    <div className="ai-generation-container">
                        <AICourseGenRow
                            isAILoading={isAILoading}
                            generateCourseWithAI={generateCourseWithAI}
                            openCourseTemplateBank={openCourseTemplateBank}
                        />
                    </div>
                )} */}

                <div className="top-line">
                    <div className="button-row">
                        {/* AI Wizard Button */}
                        {hasAIFF && (
                            <CreateWithAIButton
                                onClick={() => setIsAIWizardOpen(true)}
                                disabled={false}
                                isAILoading={isAILoading}
                            />
                        )}

                        {selectedSection && (
                            <>
                                <Button
                                    icon={<VisibilityIcon/>}
                                    onClick={toggleViewMode}
                                    color={buttonColors.YELLOW}
                                    variant="text"
                                    text={i18n.translate('weezbeeCourse.editor.switchToViewMode')}
                                />
                                <Button
                                    icon={<FullscreenIcon/>}
                                    onClick={() => setFullScreen(!isFullScreen)}
                                    color={buttonColors.YELLOW}
                                    variant="text"
                                    text={i18n.translate('weezbeeCourse.editor.exitFullscreen')}
                                />
                            </>
                        )}

                        {/* Super Admin Template Mode Toggle */}
                        {isSuperAdmin && (
                            <div className="template-mode-toggle">
                                <Button
                                    onClick={() => setIsTemplateMode(!isTemplateMode)}
                                    color={isTemplateMode ? buttonColors.GREEN : buttonColors.BLUE}
                                    variant="contained"
                                    text={isTemplateMode ? i18n.translate('weezbeeCourse.editor.switchToCourseMode') : i18n.translate('weezbeeCourse.editor.switchToTemplateMode')}
                                >
                                    {isTemplateMode ? i18n.translate('weezbeeCourse.editor.courseMode') : i18n.translate('weezbeeCourse.editor.templateMode')}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Template Data Form (only visible in template mode) - Rebuilt UI */}
                    {isSuperAdmin && isTemplateMode && (
                        <div className="template-data-form-new">
                            <div className="template-header">
                                <h2>{i18n.translate('weezbeeCourse.editor.templateDetails')}</h2>
                                <Button
                                    onClick={handleSaveTemplate}
                                    color={buttonColors.GREEN}
                                    variant="contained"
                                    className="save-template-btn"
                                    text={i18n.translate('weezbeeCourse.editor.saveTemplate')}

                                />
                            </div>

                            <div className="template-form-grid">
                                <div className="template-form-column">
                                    <div className="template-form-group">
                                        <label htmlFor="templateName">{i18n.translate('weezbeeCourse.editor.templateName')}</label>
                                        <input 
                                            id="templateName"
                                            type="text" 
                                            value={templateData.templateName} 
                                            onChange={(e) => setTemplateData({...templateData, templateName: e.target.value})}
                                            placeholder={i18n.translate('weezbeeCourse.editor.templateNamePlaceholder')}
                                        />
                                    </div>

                                    <div className="template-form-group">
                                        <label htmlFor="templateSubject">{i18n.translate('weezbeeCourse.editor.templateSubject')}</label>
                                        <input 
                                            id="templateSubject"
                                            type="text" 
                                            value={templateData.templateSubject} 
                                            onChange={(e) => setTemplateData({...templateData, templateSubject: e.target.value})}
                                            placeholder={i18n.translate('weezbeeCourse.editor.templateSubjectPlaceholder')}
                                        />
                                    </div>

                                    <div className="template-form-group">
                                        <label htmlFor="templateTags">{i18n.translate('weezbeeCourse.editor.templateTags')}</label>
                                        <input 
                                            id="templateTags"
                                            type="text" 
                                            value={templateData.tags.join(', ')} 
                                            onChange={(e) => setTemplateData({...templateData, tags: e.target.value.split(',').map(tag => tag.trim())})}
                                            placeholder={i18n.translate('weezbeeCourse.editor.templateTagsPlaceholder')}
                                        />
                                    </div>
                                </div>

                                <div className="template-form-column">
                                    <div className="template-form-group description-group">
                                        <label htmlFor="templateDescription">{i18n.translate('weezbeeCourse.editor.templateDescription', {defaultValue: 'Template Overview'})}</label>
                                        <textarea 
                                            id="templateDescription"
                                            value={templateData.description} 
                                            onChange={(e) => setTemplateData({...templateData, description: e.target.value})}
                                            placeholder={i18n.translate('weezbeeCourse.editor.templateDescriptionPlaceholder', {defaultValue: 'Provide a detailed description of what this template covers and who it\'s designed for'})}
                                        />
                                    </div>

                                    <div className="template-form-checkboxes">
                                        <div className="template-checkbox-group">
                                            <input 
                                                id="templatePublic"
                                                type="checkbox" 
                                                checked={templateData.isPubliclyAccessible} 
                                                onChange={(e) => setTemplateData({...templateData, isPubliclyAccessible: e.target.checked})}
                                            />
                                            <label htmlFor="templatePublic">
                                                {i18n.translate('weezbeeCourse.editor.templatePublic', {defaultValue: 'Make Available to All Organizations'})}
                                            </label>
                                        </div>

                                        <div className="template-checkbox-group">
                                            <input 
                                                id="templateReady"
                                                type="checkbox" 
                                                checked={templateData.isReady} 
                                                onChange={(e) => setTemplateData({...templateData, isReady: e.target.checked})}
                                            />
                                            <label htmlFor="templateReady">
                                                {i18n.translate('weezbeeCourse.editor.templateReady', {defaultValue: 'Mark as Ready for Production Use'})}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="editor-content fullWidth" ref={editorContentRef}>
                    <div
                        className="course-preview"
                        style={{width: '100%'}}
                        ref={coursePreviewRef}
                    >
                        <WeezBeeCourse
                            signDocId={signDocId}
                            key="course-preview"
                            onAddChapter={handleAddSection}
                            isSuperAdminMode={isSuperAdmin && isTemplateMode}
                            templateId={templateId}
                        />
                    </div>
                </div>
            </div>

            {/* Course Template Bank Modal */}
            {isCourseTemplateBankOpen && (
                <div className="course-template-bank-modal">
                    <CourseTemplateBank
                        onSelectCourseTemplate={handleSelectCourseTemplate}
                        onClose={closeCourseTemplateBank}
                        description={userCourseDescription}
                        signDocId={signDocId}
                    />
                </div>
            )}
        </div>
    );
};

// Main component that provides the context
export const WeezBeeCourseEditor = ({ isTemplateMode: propIsTemplateMode }) => {
    const {signDocId, templateId: routeTemplateId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [templateData, setTemplateData] = useState({
        templateName: '',
        templateSubject: '',
        description: '',
        isPubliclyAccessible: false,
        forCompanyIds: [],
        isReady: false,
        tags: [],
        estimatedDuration: 60,
        metadata: {}
    });

    // State to hold template sections
    const [templateSections, setTemplateSections] = useState([]);
    const userContext = useContext(UserReactContext);
    const {i18n} = useContext(I18nReactContext);
    const isSuperAdmin = userContext?.isUserSuperAdmin?.() || false;

    // For backward compatibility, still check query parameters
    const queryParams = new URLSearchParams(location.search);
    const queryTemplateId = queryParams.get('templateId');

    // Use the template ID from the route params, query params, or signDocId (in template mode)
    const templateId = routeTemplateId || queryTemplateId;

    // Initialize isTemplateMode based on props or query parameters (for backward compatibility)
    // In the future, we can remove the query parameter check and only use the prop
    const [isTemplateMode, setIsTemplateMode] = useState(propIsTemplateMode || (queryParams.get('templateMode') === 'true') || isSuperAdmin);

    // Fetch template metadata when in template mode (template-specific data not in weezbeeCourse)
    useEffect(() => {
        const fetchTemplateMetadata = async () => {
            if (!isTemplateMode || !templateId || templateId === "new") {
                return;
            }

            try {
                const effectiveTemplateId = templateId || signDocId;

                const templateDataFromApi = await SA_getPublicTemplateByIdAPI({ id: effectiveTemplateId });

                // Update the templateData state with the metadata from the API
                setTemplateData({
                    templateName: templateDataFromApi.templateName || '',
                    templateSubject: templateDataFromApi.templateSubject || '',
                    description: templateDataFromApi.description || '',
                    isPubliclyAccessible: templateDataFromApi.isPubliclyAccessible || false,
                    forCompanyIds: templateDataFromApi.forCompanyIds || [],
                    isReady: templateDataFromApi.isReady || false,
                    tags: templateDataFromApi.tags || [],
                    estimatedDuration: templateDataFromApi.estimatedDuration || 60,
                    metadata: templateDataFromApi.metadata || {}
                });

                // CRITICAL FIX: Load template sections into state
                // The sections need to be passed to CourseEditorProvider as initialCourseData
                if (templateDataFromApi.sections && Array.isArray(templateDataFromApi.sections)) {
                    setTemplateSections(templateDataFromApi.sections);
                } else {
                    setTemplateSections([]);
                }
            } catch (error) {
                console.error('Error fetching template metadata:', error);
                alert(i18n.translate('weezbeeCourse.editor.fetchError', {
                    defaultValue: 'Error loading template. Please try again.'
                }));
            }
        };

        fetchTemplateMetadata();
    }, [signDocId, isTemplateMode, templateId, i18n]);

    // Function to handle creating a new template
    const handleCreateNewTemplate = async () => {
        try {
            // Create a new template with a proper ID
            const newTemplate = await SA_createPublicTemplateAPI({
                templateName: 'New Template',
                templateSubject: 'General',
                description: '',
                isPubliclyAccessible: false,
                isReady: false,
                tags: [],
                estimatedDuration: 60,
                sections: []
            });

            // Navigate to the new template with its ID
            if (newTemplate && newTemplate.id) {
                // Use the new dedicated route for template editing
                navigate(`/nihol/courseTemplateEditor/${newTemplate.id}/edit`);
            }
        } catch (error) {
            console.error('Error creating new template:', error);
            alert(i18n.translate('weezbeeCourse.editor.templateCreateError', {
                defaultValue: 'Error creating new template. Please try again.'
            }));
        }
    };

    // Template selector UI
    const renderTemplateSelector = () => {
        return (
            <div className="template-selector">
                <h2>{i18n.translate('weezbeeCourse.editor.templateSelectorTitle', {
                    defaultValue: 'Create or Select a Template'
                })}</h2>

                <div className="template-selector-options">
                    <div className="template-selector-option">
                        <h3>{i18n.translate('weezbeeCourse.editor.createNewTemplate', {
                            defaultValue: 'Create New Template'
                        })}</h3>
                        <p>{i18n.translate('weezbeeCourse.editor.createNewTemplateDesc', {
                            defaultValue: 'Start with a blank template and build it from scratch.'
                        })}</p>
                        <Button
                            onClick={handleCreateNewTemplate}
                            color={buttonColors.GREEN}
                            variant="contained"
                        >
                            {i18n.translate('weezbeeCourse.editor.createTemplate', {
                                defaultValue: 'Create Template'
                            })}
                        </Button>
                    </div>

                    <div className="template-selector-option">
                        <h3>{i18n.translate('weezbeeCourse.editor.selectExistingTemplate', {
                            defaultValue: 'Select Existing Template'
                        })}</h3>
                        <p>{i18n.translate('weezbeeCourse.editor.selectExistingTemplateDesc', {
                            defaultValue: 'Choose from your existing templates to edit.'
                        })}</p>
                        <Button
                            onClick={() => navigate('/nihol/courseTemplates')}
                            color={buttonColors.BLUE}
                            variant="contained"
                        >
                            {i18n.translate('weezbeeCourse.editor.browseTemplates', {
                                defaultValue: 'Browse Templates'
                            })}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {(signDocId === "new" || location.pathname === "/nihol/courseTemplateEditor/new") && isTemplateMode && isSuperAdmin ? (
                // Show template selector when signDocId is "new" or we're on the new template route
                renderTemplateSelector()
            ) : (
                <CourseEditorProvider
                    signDocId={signDocId}
                    initialMode={COURSE_MODES.EDIT}
                    isTemplateMode={isTemplateMode}
                    templateId={templateId}
                    initialCourseData={isTemplateMode ? templateSections : []}
                >
                    <WeezBeeCourseEditorContent
                        courseTitle={templateData.templateName}
                        courseDescription={templateData.description}
                        signDocId={signDocId}
                        isTemplateMode={isTemplateMode}
                        setIsTemplateMode={setIsTemplateMode}
                        templateData={templateData}
                        setTemplateData={setTemplateData}
                    />
                </CourseEditorProvider>
            )}
        </>
    );
}; 
